import { useEffect, useState } from "react";
import { catalogAPI } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";

function Combobox({ value, onChange, options, placeholder, testId, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const hasExact = options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
          data-testid={testId}
        >
          <span className={value ? "" : "text-muted-foreground"}>{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search or type..." value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>
              {query.trim() ? (
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-secondary"
                  onClick={() => { onChange(query.trim()); setOpen(false); }}
                >
                  <Plus className="h-4 w-4" /> Use &quot;{query.trim()}&quot;
                </button>
              ) : (
                <span className="px-3 py-2 text-sm text-muted-foreground">No matches</span>
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => { onChange(opt); setOpen(false); }}
                  data-testid={`${testId}-option`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === opt ? "opacity-100" : "opacity-0"}`} />
                  {opt}
                </CommandItem>
              ))}
              {query.trim() && !hasExact && (
                <CommandItem value={`__custom_${query}`} onSelect={() => { onChange(query.trim()); setOpen(false); }}>
                  <Plus className="mr-2 h-4 w-4" /> Use &quot;{query.trim()}&quot;
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Cascading manufacturer -> product selector backed by the central catalog.
 * Free-text fallback allowed for both fields.
 * Props: manufacturer, product, onChange({manufacturer, product}), allowGeneral
 */
export default function ProductSelector({
  manufacturer = "",
  product = "",
  onChange,
  allowGeneral = false,
  required = false,
  labels = { manufacturer: "Manufacturer", product: "Product" },
  testIdPrefix = "product",
}) {
  const [catalog, setCatalog] = useState({ manufacturers: [], grouped: {} });
  const isGeneral = allowGeneral && manufacturer === "" && product === "General";

  useEffect(() => {
    catalogAPI.getProducts()
      .then((res) => setCatalog({ manufacturers: res.data.manufacturers || [], grouped: res.data.grouped || {} }))
      .catch(() => {});
  }, []);

  const productOptions = manufacturer && catalog.grouped[manufacturer]
    ? catalog.grouped[manufacturer].map((p) => p.product_name)
    : [];

  return (
    <div className="space-y-3">
      {allowGeneral && (
        <label className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`${testIdPrefix}-general`}>
          <input
            type="checkbox"
            checked={isGeneral}
            onChange={(e) => onChange(e.target.checked ? { manufacturer: "", product: "General" } : { manufacturer: "", product: "" })}
            className="h-4 w-4 accent-[hsl(var(--primary))]"
          />
          This post isn&apos;t about a specific product (General)
        </label>
      )}
      {!isGeneral && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{labels.manufacturer}{required && " *"}</Label>
            <Combobox
              value={manufacturer}
              onChange={(v) => onChange({ manufacturer: v, product: "" })}
              options={catalog.manufacturers}
              placeholder="Select manufacturer"
              testId={`${testIdPrefix}-manufacturer`}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{labels.product}{required && " *"}</Label>
            <Combobox
              value={product}
              onChange={(v) => onChange({ manufacturer, product: v })}
              options={productOptions}
              placeholder={manufacturer ? "Select product" : "Choose manufacturer first"}
              testId={`${testIdPrefix}-product`}
              disabled={!manufacturer}
            />
          </div>
        </div>
      )}
    </div>
  );
}
